import { Box, Chip } from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as constants from "../../../constants";
import environment from "../../../environments/environment";
import CustomAutocomplete from "../../../shared/AutoComplete";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../shared/Button/customOutlinedButton";
import CustomDatePicker from "../../../shared/CustomDate";
import CustomTextFieldComponent from "../../../shared/CustomTextFeild";
import { QuickbookTransactionDetails } from "../../../shared/JsonTooltipContent";
import ReusableTable from "../../../shared/ReusableTable/ReusableTable";
import { RootState } from "../../../store/store";
import { resetTableState, setPagination, setTableFilters } from "../../../store/tableSlice";
import basestyle from "../../CSS/Base.module.css";
import TransactionDetailsPopUp from "../../PopUp/TransactionDetailsPopUp";
import {
  SectionTitle,
  StyledOneChildContainer,
  StyledParentContainer,
  StyledTwoChildContainer,
} from "../StyledComponents/Container";
import CommonUtilities from "../Utils/CommonUtilities";
import { formatDateTime } from "../Utils/DataFormat";
import { notifyUser } from "../Utils/notifyUser";
import { getAllParticipants } from "../services/MonthlyCalculations/monthlyServices";
import { axiosInterceptor } from "../services/authInterceptor";
import { formatCurrency } from "../Utils/formatUtils";
import { setActiveIndex, setOpenMenuIndex } from "../../../store/MenutItems/menuUiSlice";
import { getClientDetailsByClientName } from "../Utils/ClientSettingsTable";

const types = CommonUtilities.responseTypesOptions;
const statusOptions = CommonUtilities.blackBaudStatusOptions;

interface Column {
  id: string;
  label: string;
  minWidth: number | string;
  align?: "center" | "left" | "right";
}

interface Participant {
  participantId: number;
  participantName: string;
}

const formattedDate = (dateString: string): string => {
  const date = new Date(dateString + "T00:00:00");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};



const BlackBaudLog = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const userInfo = useSelector((state: RootState) => state.auth);
  const ClientDetails = getClientDetailsByClientName(userInfo?.clientVo?.name);
  const contactInformation = useSelector((state: RootState) => state.participantAdminProfile);
  const [userRole] = useState<string>(contactInformation.participantAdminProfile);

  const retain = useSelector(
    (state: RootState) => state.table.blackBaudLog
  );

  const [page, setPage] = useState(retain?.page || 1);
  const [rowsPerPage, setRowsPerPage] = useState(retain?.pageSize || 5);
  const [fromDate, setFromDate] = useState<string | null>(retain?.filters.fromDate || null);
  const [toDate, setToDate] = useState<string | null>(retain?.filters.toDate || null);
  const [selectedType, setSelectedType] = useState(retain?.filters.selectedType || "All");
  const [selectedStatus, setSelectedStatus] = useState(retain?.filters.selectedStatus || "All");
  const [selectedCorrelationId, setSelectedCorrelationId] = useState<string>(retain?.filters?.selectedCorrelationId ?? "");
  const [selectedParticipant, setSelectedParticipant] = useState<Participant>(retain?.filters.selectedParticipant || { participantId: 0, participantName: "All", });
  const [popupOpen, setPopupOpen] = React.useState(false);
  const [popupData, setPopupData] = React.useState<any>(null);
  const [count, setCount] = useState(0);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [participantArray, setParticipantArray] = useState<Participant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const renderDashCentered = (value: any) => {
    if (!value || value === "-") {
      return <div style={{ textAlign: "center" }}>-</div>;
    }
    return value;
  };
  const columns: Column[] = [
    { id: "transactionDate", label: translate("transactionDate"), minWidth: 230, align: "center" },
    { id: "payload", label: translate("payload"), minWidth: 200, align: "left" },
    { id: "type", label: translate("type"), minWidth: 150, align: "left" },
    { id: "status", label: translate("status"), minWidth: 150, align: "left" },
    { id: "correlationId", label: translate("correlationId"), minWidth: 230, align: "left" },
    // ...(userRole === constants.ADMIN_VIEW
    //   ? [{ id: "participantName", label: translate("participantName"), minWidth: "18%" }]
    //   : []),
    { id: "userId", label: translate("userId"), minWidth: 250, align: "left" },
  ];

  const handlePageChange = (newPage: number) => setPage(newPage + 1);
  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };
  const handleBack = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (contactInformation.participantAdminProfile === constants.ADMIN_VIEW) {
      dispatch(setActiveIndex(0));
      dispatch(setOpenMenuIndex(null));
      if ((ClientDetails?.name === constants.CLIENT_NAME_DC_GARY && userInfo?.roles?.roleName === constants.ROLES_ADMIN_MANAGER)) {
        navigate(constants.USER_LISTING_PATH);
      } else {
        navigate("/rcboSummary");
      }
    } else {
      dispatch(setActiveIndex(0));
      dispatch(setOpenMenuIndex(null));
      navigate("/summary");
    }
  };

  const handleSearch = () => {
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      if (to < from) {
        toast.error(translate("toDateShouldBeGreaterThanFromDate"));
        return;
      }
    }
    setPage(1);
    getBlackBaudLogListInfo(
      fromDate,
      toDate,
      selectedType,
      selectedStatus,
      selectedCorrelationId,
      selectedParticipant,
      1,
      rowsPerPage
    );
  };

  const handleClear = () => {
    dispatch(resetTableState({ tableKey: "blackBaudLog" }));
    setFromDate(null);
    setToDate(null);
    setSelectedType("All");
    setSelectedStatus("All");
    setSelectedCorrelationId("");
    setSelectedParticipant({ participantId: 0, participantName: "All" });
    setPage(1);
    setRowsPerPage(5);

    getBlackBaudLogListInfo(null, null, "All", "All", "", { participantId: 0, participantName: "All" }, 1, 5);
  };
  const handlePopupClose = () => {
    setPopupOpen(false);
  };

  async function getBlackBaudLogListInfo(
    fromDate: string | null,
    toDate: string | null,
    selectedType: string,
    selectedStatus: string,
    selectedCorrelationId: string,
    selectedParticipant: Participant,
    page: number,
    rowsPerPage: number
  ) {
    setIsLoading(true);
    try {
      const payload = {
        clientId: userInfo.clientId,
        participantId:
          userRole === constants.ADMIN_VIEW
            ? selectedParticipant?.participantId
            : userInfo?.defaultParticipantId,
        type: selectedType === "All" ? "" : selectedType,
        status: selectedStatus === "All" ? "" : selectedStatus,
        correlationId: selectedCorrelationId || "",
        fromDate: fromDate ? new Date(fromDate).toISOString().replace("Z", "") : "",
        toDate: toDate ? new Date(toDate).toISOString().replace("Z", "") : "",
        page,
        size: rowsPerPage,
      };

      dispatch(
        setTableFilters({
          tableKey: "blackBaudLog",
          filters: {
            fromDate,
            toDate,
            selectedType,
            selectedStatus,
            selectedCorrelationId,
            selectedParticipant,
          },
        })
      );
      dispatch(
        setPagination({
          tableKey: "blackBaudLog",
          page,
          pageSize: rowsPerPage,
        })
      );

      const response = await axiosInterceptor.post(
        environment.postBlackBaudLogFilter,
        payload
      );

      if (response.status === constants.STATUS_CODES.SUCCESS) {
        setCount(response.data.totalRecordCount);
        const updatedData = response.data.data?.map((item: any) => ({
          ...item,
          transactionDate: formattedDate(item.transactionDate),
          amount: formatCurrency(item.amount),
        }));
        setFilteredData(updatedData || []);
      } else {
        setCount(0);
        setFilteredData([]);
        notifyUser(response.data.message, "error", translate("blackBaudLog"));
      }
    } catch (error) {
      console.error("Error fetching Blackbaud Log data:", error);
    } finally {
      setIsLoading(false);
    }
  }

  async function getAllParticipantsList() {
    setIsLoading(true);
    try {
      const request = {
        participantName: "",
        isActive: constants.IS_ACTIVE_YES,
        clientId: userInfo.clientId,
      };
      const response = await getAllParticipants(request);

      if (response.status === constants.STATUS_CODES.SUCCESS) {
        const participants = response.data.data.map((p: any) => ({
          participantId: p.participantId,
          participantName: p.participantName,
        }));
        setParticipantArray([{ participantId: 0, participantName: "All" }, ...participants]);
      } else {
        setParticipantArray([]);
      }
    } catch (error) {
      console.error("Error fetching Participants List:", error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getAllParticipantsList();
  }, []);

  useEffect(() => {
    getBlackBaudLogListInfo(
      fromDate,
      toDate,
      selectedType,
      selectedStatus,
      selectedCorrelationId,
      selectedParticipant,
      page,
      rowsPerPage
    );
  }, [page, rowsPerPage]);

  const handleKeyPress = (event: any) => {
    if (event.key === "Enter") handleSearch();
  };
  const handleRowClick = (list: any) => {
    setPopupData(list);
    setPopupOpen(true);
  };

  const blackBaudTableList = filteredData?.map((list: any) => ({
    ...list,
    transactionDate: renderDashCentered(formatDateTime(list?.submitTime)),
    payload:
      list?.payload && list.payload.length > 40 ? (
        <span
          onClick={() => handleRowClick(list)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            whiteSpace: "nowrap",
          }}
        >
          {list.payload.slice(0, 52) + "..."}
          <Chip
            label="More"
            size="small"
            variant="outlined"
            color="primary"
            onClick={(e: any) => {
              e.stopPropagation();
              handleRowClick(list);
            }}
          />
        </span>
      ) : (
        list?.payload
      ),
    type: renderDashCentered(list?.type),
    correlationId: renderDashCentered(list?.correlationId),
    userId: renderDashCentered(list?.createdBy),
  }));

  return (
    <StyledParentContainer>
      <StyledOneChildContainer>
        <StyledTwoChildContainer sx={{ border: "none" }}>
          <SectionTitle>{translate("blackBaudLog")}</SectionTitle>
          <Box sx={{ width: "100%", px: 1.5 }}>
            <div className="search-fields-container">
              <div className="left-fields">
                <CustomTextFieldComponent
                  label={translate("correlationId")}
                  sx={{ width: { xs: "100%", sm: 250 } }}
                  className="small-text-field"
                  size="small"
                  value={selectedCorrelationId}
                  onKeyDown={handleKeyPress}
                  onChange={(e) =>
                    setSelectedCorrelationId(e.target.value.replace(/\s/g, ""))
                  }
                />
                <CustomDatePicker
                  sx={{ width: { xs: "100%", sm: 180 } }}
                  label={translate("fromDate")}
                  value={fromDate ? dayjs(fromDate) : null}
                  onChange={(newValue) =>
                    setFromDate(newValue ? newValue.format("YYYY-MM-DD") : null)
                  }
                />
                <CustomDatePicker
                  sx={{ width: { xs: "100%", sm: 180 } }}
                  label={translate("toDate")}
                  value={toDate ? dayjs(toDate) : null}
                  onChange={(newValue) =>
                    setToDate(newValue ? newValue.format("YYYY-MM-DD") : null)
                  }
                />
                {/* {userRole === constants.ADMIN_VIEW && (
                  <CustomAutocomplete
                    size="small"
                    sx={{ width: { xs: "100%", sm: 250 } }}
                    options={participantArray}
                    value={selectedParticipant}
                    onChange={(event, newValue) => {
                      if (!newValue) return;
                      setSelectedParticipant(newValue);
                      setPage(1);
                      getBlackBaudLogListInfo(
                        fromDate,
                        toDate,
                        selectedType,
                        selectedStatus,
                        selectedCorrelationId,
                        newValue,
                        1,
                        rowsPerPage
                      );
                    }}
                    getOptionLabel={(option) => option.participantName}
                    isOptionEqualToValue={(option, value) =>
                      option.participantId === value?.participantId
                    }
                    label={translate("participant")}
                    placeholder={translate("participant")}
                  />
                )} */}
                <CustomAutocomplete
                  sx={{ width: { xs: "100%", sm: 250 } }}
                  options={types}
                  value={selectedType}
                  onChange={(event, newValue) => {
                    if (!newValue) return;
                    setSelectedType(newValue);
                    setPage(1);
                    getBlackBaudLogListInfo(
                      fromDate,
                      toDate,
                      newValue,
                      selectedStatus,
                      selectedCorrelationId,
                      selectedParticipant,
                      1,
                      rowsPerPage
                    );
                  }}
                  label={translate("type")}
                />
                <CustomAutocomplete
                  sx={{ width: { xs: "100%", sm: 250 } }}
                  options={statusOptions}
                  value={selectedStatus}
                  onChange={(event, newValue) => {
                    if (!newValue) return;
                    setSelectedStatus(newValue);
                    setPage(1);
                    getBlackBaudLogListInfo(
                      fromDate,
                      toDate,
                      selectedType,
                      newValue,
                      selectedCorrelationId,
                      selectedParticipant,
                      1,
                      rowsPerPage
                    );
                  }}
                  label={translate("status")}
                />
                <ContainedButton onClick={handleSearch}>{translate('search')}</ContainedButton>
                <OutlinedButton onClick={handleClear}>{translate('clear')}</OutlinedButton>
              </div>
            </div>
            <ReusableTable
              columns={columns}
              rows={blackBaudTableList}
              count={count}
              page={page - 1}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              loading={isLoading}
            />

            <div className={basestyle.buttoncontainer}>
              <ContainedButton onClick={handleBack}>
                {" "}
                {translate("back")}{" "}
              </ContainedButton>
            </div>
            <TransactionDetailsPopUp
              open={popupOpen}
              handleClose={handlePopupClose}
              message={
                popupData ? (
                  <QuickbookTransactionDetails totalList={popupData} title={translate("transactionDetails")} />
                ) : ""
              }
              popupTitle={translate("transactionDetails")}
            />
          </Box>
        </StyledTwoChildContainer>
      </StyledOneChildContainer>
    </StyledParentContainer>
  );
};

export default BlackBaudLog;
